import React, { useEffect, useState } from "react";
import {
    ShieldCheck,
    SquarePen,
    Save,
    X,
} from "lucide-react";

import { toast } from "react-toastify";
import { postData1 } from "../../../services";

const DetailItem = ({
    label,
    name,
    value,
    textarea = false,
    select = false,
    options = [],
    type = "text",
    isEditing,
    handleChange,
}) => {

    const getBadgeClass = () => {
        if (name === "lead_status") {
            switch (value?.toLowerCase()) {
                case "hot":
                    return "bg-red-50 text-red-600 border-red-200";
                case "warm":
                    return "bg-yellow-50 text-yellow-700 border-yellow-200";
                case "cold":
                    return "bg-blue-50 text-blue-700 border-blue-200";
                default:
                    return "bg-gray-50 text-gray-600 border-gray-200";
            }
        }
        switch (value?.toUpperCase()) {
            case "A":
                return "bg-green-50 text-green-700 border-green-200";
            case "B":
                return "bg-yellow-50 text-yellow-700 border-yellow-200";
            case "C":
                return "bg-orange-50 text-orange-700 border-orange-200";
            case "D":
                return "bg-red-50 text-red-700 border-red-200";
            default:
                return "bg-gray-50 text-gray-700 border-gray-200";
        }
    };

    return (

        <div className="pb-5 border-b border-gray-200">

            <p className="text-[14px] font-semibold uppercase text-[#93A1BD] mb-2">
                {label}
            </p>

            {isEditing ? (

                select ? (

                    <select
                        name={name}
                        value={value}
                        onChange={handleChange}
                        className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:ring-2 focus:ring-teal-500 outline-none"
                    >

                        {options.map((item) => (

                            <option
                                key={item.value}
                                value={item.value}
                            >
                                {item.label}
                            </option>

                        ))}

                    </select>

                ) : textarea ? (

                    <textarea
                        rows={4}
                        name={name}
                        value={value}
                        onChange={handleChange}
                        className="w-full rounded-lg border border-gray-300 p-3 focus:ring-2 focus:ring-teal-500 outline-none resize-none"
                    />

                ) : (

                    <input
                        type={type}
                        name={name}
                        value={value}
                        onChange={handleChange}
                        className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:ring-2 focus:ring-teal-500 outline-none"
                    />

                )

            ) : (

                (name === "customer_status" || name === "lead_status") ? (

                    <span
                        className={`inline-flex items-center px-4 py-1.5 rounded-lg border text-sm font-semibold ${getBadgeClass()}`}
                    >
                        {value
                            ? value.charAt(0).toUpperCase() + value.slice(1)
                            : "-"}
                    </span>

                ) : (

                    <p className="text-[17px] font-medium text-slate-900 break-words">
                        {value || "-"}
                    </p>

                )
            )}

        </div>

    );

};

const AdminActions = ({
    userData,
    getUserDetails,
}) => {

    const [isEditing, setIsEditing] = useState(false);

    const [formData, setFormData] = useState({

        customer_status: "",
        follow_up_tracker: "",
        lead_status: "",
        next_followup_date: "",
        assigned_sales_manager: "",
        internal_remarks: "",
        risk_flags: "",

    });

    useEffect(() => {

        if (userData?.profile) {

            setFormData({
                customer_status: userData.profile.customer_status || "",
                follow_up_tracker: userData.profile.follow_up_tracker || "",
                lead_status: userData.profile.lead_status || "",
                next_followup_date: userData.profile.next_followup_date || "",
                assigned_sales_manager: userData.profile.assigned_sales_manager || "",
                internal_remarks: userData.profile.internal_remarks || "",
                risk_flags: userData.profile.risk_flags || "",

            });

        }

    }, [userData]);

    const handleChange = (e) => {

        const { name, value } = e.target;

        setFormData((prev) => ({
            ...prev,
            [name]: value,
        }));

    };


    const leadStatusOptions = [
        { value: "", label: "Select" },
        { value: "hot", label: "Hot" },
        { value: "warm", label: "Warm" },
        { value: "cold", label: "Cold" },
    ];

    const handleCancel = () => {

        setIsEditing(false);

        setFormData({

            customer_status: userData?.profile?.customer_status || "",
            follow_up_tracker: userData?.profile?.follow_up_tracker || "",
            lead_status: userData?.profile?.lead_status || "",
            next_followup_date: userData?.profile?.next_followup_date || "",
            assigned_sales_manager: userData?.profile?.assigned_sales_manager || "",
            internal_remarks: userData?.profile?.internal_remarks || "",
            risk_flags: userData?.profile?.risk_flags || "",

        });

    };

    const handleSave = async () => {

        try {

            const body = {
                user_id: userData?.id,
                customer_status: formData.customer_status,
                follow_up_tracker: formData.follow_up_tracker,
                lead_status: formData.lead_status,
                next_followup_date: formData.next_followup_date,
                assigned_sales_manager: formData.assigned_sales_manager,
                internal_remarks: formData.internal_remarks,
                risk_flags: formData.risk_flags,

            };

            const res = await postData1(

                "admin/update_profile/",

                body,

                true,

                true

            );

            if (res?.status_code === 200) {

                toast.success("Admin actions updated successfully.");

                setIsEditing(false);

                getUserDetails &&
                    getUserDetails();

            } else {

                toast.error(
                    res?.msg || "Something went wrong."
                );

            }

        } catch (error) {

            console.log(error);

            toast.error(
                "Failed to update admin actions."
            );

        }

    };

    return (

        <div className="bg-white rounded-[24px] border border-gray-200 shadow-sm overflow-hidden">

            {/* Header */}

            <div className="flex items-center justify-between px-8 py-6 border-b border-gray-200">

                <div className="flex items-center gap-3">

                    <div className="w-11 h-11 rounded-xl bg-orange-100 flex items-center justify-center">

                        <ShieldCheck
                            size={20}
                            className="text-orange-600"
                        />

                    </div>

                    <h2 className="text-[18px] font-bold uppercase text-slate-800">
                        Admin Actions
                    </h2>

                </div>

                {!isEditing ? (

                    <button
                        onClick={() => setIsEditing(true)}
                        className="p-2 rounded-lg hover:bg-gray-100"
                    >
                        <SquarePen size={18} />
                    </button>

                ) : (

                    <div className="flex gap-2">

                        <button
                            onClick={handleSave}
                            className="flex items-center gap-2 bg-teal-600 hover:bg-teal-700 text-white px-4 py-2 rounded-lg"
                        >
                            <Save size={16} />
                            Save
                        </button>

                        <button
                            onClick={handleCancel}
                            className="flex items-center gap-2 bg-red-500 hover:bg-red-600 text-white px-4 py-2 rounded-lg"
                        >
                            <X size={16} />
                            Cancel
                        </button>

                    </div>

                )}

            </div>

            {/* Body */}

            <div className="p-8">

                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-8">

                    <DetailItem
                        label="Customer Status"
                        name="customer_status"
                        value={formData.customer_status}
                        isEditing={isEditing}
                        handleChange={handleChange}
                    />

                    <DetailItem
                        label="Lead Status"
                        name="lead_status"
                        value={formData.lead_status}
                        select
                        options={leadStatusOptions}
                        isEditing={isEditing}
                        handleChange={handleChange}
                    />

                    <DetailItem
                        label="Assigned Sales Manager"
                        name="assigned_sales_manager"
                        value={formData.assigned_sales_manager}
                        isEditing={isEditing}
                        handleChange={handleChange}
                    />
                    <DetailItem
                        label="Next Follow-up Date"
                        name="next_followup_date"
                        value={formData.next_followup_date}
                        type="date"
                        isEditing={isEditing}
                        handleChange={handleChange}
                    />

                    <DetailItem
                        label="Follow-up Tracker"
                        name="follow_up_tracker"
                        value={formData.follow_up_tracker}
                        textarea
                        isEditing={isEditing}
                        handleChange={handleChange}
                    />

                    <DetailItem
                        label="Risk Flags / Credit Issues"
                        name="risk_flags"
                        value={formData.risk_flags}
                        textarea
                        isEditing={isEditing}
                        handleChange={handleChange}
                    />

                </div>

                <div className="mt-8">

                    <DetailItem
                        label="Internal Remarks"
                        name="internal_remarks"
                        value={formData.internal_remarks}
                        textarea
                        isEditing={isEditing}
                        handleChange={handleChange}
                    />

                </div>

            </div>

        </div>

    );

};

export default AdminActions;